import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'

export type FeatureName = 'kuper' | 'gigachat' | 'lkPromotion' | 'ownerBooking' | 'SberID'

export type FeatureFlags = Record<FeatureName, boolean>

export const defaultFeatureFlags: FeatureFlags = {
  kuper: false,
  gigachat: false,
  lkPromotion: false,
  ownerBooking: false,
  SberID: false,
}

export const useFeatureToggle = () => {
  const [flags, setFlags] = useState<FeatureFlags>(defaultFeatureFlags)
  const searchParams = useSearchParams()
  const router = useRouter()

  useEffect(() => {
    const flagsFromSearchParams = searchParams.get('featureFlags')
      ? searchParams
          .get('featureFlags')
          .split(',')
          .reduce((acc, flagName) => {
            return flagName in defaultFeatureFlags ? { ...acc, [flagName]: true } : acc
          }, defaultFeatureFlags)
      : defaultFeatureFlags

    setFlags(flagsFromSearchParams)
  }, [searchParams.toString()])

  const toggleFeatureFlag = useCallback(
    (featureName: FeatureName) => {
      if (typeof window === 'undefined') {
        return
      }

      setFlags((oldFlags) => {
        const newFlags = { ...oldFlags, [featureName]: !oldFlags[featureName] }

        const newUrlParams = new URLSearchParams(searchParams.toString())

        const activeFlags = Object.keys(newFlags).filter((key) => newFlags[key])

        if (activeFlags.length > 0) {
          newUrlParams.set('featureFlags', activeFlags.join(','))
        } else {
          newUrlParams.delete('featureFlags')
        }

        const newUrl = `${window.location.pathname}?${newUrlParams.toString()}${window.location.hash}`
        router.replace(newUrl, undefined, { shallow: true })

        return newFlags
      })
    },
    [searchParams.toString()],
  )

  return {
    flags,
    toggleFeatureFlag,
  }
}
